<template>
    <v-app :theme="themeName">
        <!-- Global Loading Dialog -->
        <v-dialog v-model="isLoading" :scrim="true" persistent width="auto" style="z-index: 20001">
            <v-card elevation="10">
                <v-card-text>
                    {{ $t('Please wait') }}
                    <v-progress-linear indeterminate rounded color="secondary" />
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Top error alert -->
        <v-alert v-if="topError" type="error" outlined dense class="ma-4" dismissible @input="topError = ''">
            {{ topError }}
        </v-alert>

        <!-- Smooth Route Transition -->
        <transition name="fade-slide" mode="out-in">
            <div v-if="appReady">
                <keep-alive>
                    <router-view v-slot="{ Component }">
                        <component :is="Component" />
                    </router-view>
                </keep-alive>
            </div>
            <div v-else class="flex items-center justify-center h-screen">
                <v-progress-circular indeterminate color="primary" />
            </div>
        </transition>
    </v-app>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref, watch } from 'vue'
    import { useLoadingState } from '@/stores/loadingState'
    import axios from '@/utils/apiClient'
    import { useRouter } from 'vue-router'
    import Swal from 'sweetalert2'
    import { useCompanyStore } from '@/stores/companyStore'
    import { useCustomizerStore, initCustomizerPersistence } from '@/stores/customizerStore'
    import { useAuthStore } from '@/stores/auth'
    import { useTheme } from 'vuetify'
    import { storeToRefs } from 'pinia'

    // Stores
    const loadingState = useLoadingState()
    const companyStore = useCompanyStore()
    const customizerStore = useCustomizerStore()
    const authStore = useAuthStore()
    const router = useRouter()
    const vuetifyTheme = useTheme()
    initCustomizerPersistence()

    // Reactive state
    const isLoading = computed(() => loadingState.isLoading)
    const { theme: themeName } = storeToRefs(customizerStore)
    const topError = ref('')
    const appReady = ref(false)

    // Watch for theme change
    watch(
        themeName,
        newTheme => {
            vuetifyTheme.global.name.value = newTheme
            localStorage.setItem('theme', newTheme)
        },
        { immediate: true },
    )

    // Watch for company theme color changes and update Vuetify and CSS variables
    watch(
        () => [companyStore.company?.primaryColor, companyStore.company?.secondaryColor],
        ([newPrimary, newSecondary]) => {
            const primary = newPrimary || '#fcad64'
            const secondary = newSecondary || '#3A7DFF'
            
            // 1. Update global CSS variables
            document.documentElement.style.setProperty('--company-primary-color', primary)
            document.documentElement.style.setProperty('--company-secondary-color', secondary)
            
            // 2. Compute light tints for primary/secondary
            const hexToRgb = (hex: string) => {
                const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
                return result
                    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
                    : { r: 252, g: 173, b: 100 }
            }
            const blendWithWhite = (hex: string, alpha: number) => {
                const { r, g, b } = hexToRgb(hex)
                const r2 = Math.round(r * alpha + 255 * (1 - alpha))
                const g2 = Math.round(g * alpha + 255 * (1 - alpha))
                const b2 = Math.round(b * alpha + 255 * (1 - alpha))
                return `#${r2.toString(16).padStart(2, '0')}${g2.toString(16).padStart(2, '0')}${b2.toString(16).padStart(2, '0')}`
            }
            const lightPrimary = blendWithWhite(primary, 0.12)
            const lightSecondary = blendWithWhite(secondary, 0.12)
            
            // 3. Update Vuetify runtime theme colors reactively
            if (vuetifyTheme.current.value) {
                vuetifyTheme.current.value.colors.primary = primary
                vuetifyTheme.current.value.colors.secondary = secondary
                vuetifyTheme.current.value.colors.lightprimary = lightPrimary
                vuetifyTheme.current.value.colors.lightsecondary = lightSecondary
            }
        },
        { immediate: true, deep: true }
    )

    // Unified Error Handler
    const handleError = (error: any) => {
        const status = error?.response?.status
        let message = 'Unknown error'

        try {
            if (error?.response?.data?.errors?.general) message = error.response.data.errors.general
            else if (typeof error?.response?.data?.data === 'string') message = error.response.data.data
            else if (typeof error?.response?.data === 'string') message = error.response.data
            else if (typeof error?.response?.data?.message === 'string') message = error.response.data.message
            else if (typeof error?.message === 'string') message = error.message
        } catch (e) {
            message = error.message || 'Unknown error'
        }
        message = String(message).substring(0, 300)
        if (message.includes('DELETE statement conflicted')) message = 'Record is in use'
        if (status === 405) message = 'Method Not Allowed'

        Swal.fire({
            icon: status === 400 ? 'warning' : 'error',
            confirmButtonColor: 'red',
            html: `<div class="text-subtitle-2 pa-2 bg-lightprimary">${message}</div>`,
        })
    }

    // Axios interceptors
    const setupInterceptors = () => {
        axios.interceptors.request.use(
            config => {
                if (config.headers?.['X-Skip-Loading'] !== 'true' && !(config as any).skipLoading) {
                    loadingState.startLoading()
                }
                return config
            },
            error => {
                if (error.config?.headers?.['X-Skip-Loading'] !== 'true' && !error.config?.skipLoading) {
                    loadingState.stopLoading()
                }
                handleError(error)
                return Promise.reject(error)
            },
        )

        axios.interceptors.response.use(
            response => {
                if (response.config?.headers?.['X-Skip-Loading'] !== 'true' && !(response.config as any).skipLoading) {
                    loadingState.stopLoading()
                }
                return response
            },
            error => {
                if (error.config?.headers?.['X-Skip-Loading'] !== 'true' && !error.config?.skipLoading) {
                    loadingState.stopLoading()
                }
                handleError(error)
                return Promise.reject(error)
            },
        )
    }

    // Configuration and Session init
    const initConfiguration = async () => {
        try {
            const res = await axios.get('configurations')
            if (res.data.company) await companyStore.initializeCompanies(res.data.company)
            if (res.data.theme && !companyStore.initialized) customizerStore.SET_THEME(res.data.theme)
        } catch (error) {
            console.error('Configuration initialization failed:', error)
            customizerStore.SET_THEME('light')
        }
    }

    onMounted(async () => {
        setupInterceptors()

        await initConfiguration()

        // 🧩 Restore user session if available
        const storedUser = localStorage.getItem('user')
        if (storedUser && !authStore.user) {
            try {
                authStore.user = JSON.parse(storedUser)
            } catch {
                authStore.clearSession()
            }
        }

        appReady.value = true

        // ✅ Redirect if at root (/) after init
        if (router.currentRoute.value.path === '/' && authStore.user?.homePage) {
            router.replace(authStore.user.homePage)
        }
    })
</script>

<style scoped>
    .fade-slide-enter-active,
    .fade-slide-leave-active {
        transition:
            opacity 0.25s ease,
            transform 0.25s ease;
    }
    .fade-slide-enter-from {
        opacity: 0;
        transform: translateY(5px);
    }
    .fade-slide-leave-to {
        opacity: 0;
        transform: translateY(-5px);
    }
</style>
